import { formatMoney, type Decimal, type Quote } from '@greencandle/api-client'
import { Unknown } from './Figure'

/**
 * FR-D2's acceptance criterion, which is about honesty rather than about the number.
 *
 * <p>A quote from a free equity feed is a quarter of an hour old. Drawn as a bare figure it
 * reads as the price right now, and someone will act on it. So the delay and the moment it
 * was true are printed with it, always — not on hover, not in a tooltip.
 *
 * <p>With no quote the stored close is shown instead, which is what every figure derived
 * from it already uses. That is the ordinary case out of market hours and is not a failure.
 */
export function QuotedPrice({
  close,
  currency,
  quote,
  reason,
}: {
  close: Decimal | null
  currency: string
  quote: Quote | undefined
  reason?: string | null
}) {
  if (quote) {
    return (
      <>
        <span className="num">{formatMoney(quote.price, quote.currency)}</span>
        <div className="faint quote-age">{describe(quote)}</div>
      </>
    )
  }

  if (close == null) {
    return <Unknown reason={reason} />
  }

  return (
    <>
      <span className="num">{formatMoney(close, currency)}</span>
      <div className="faint quote-age">close</div>
    </>
  )
}

/** "delayed 15m · 14:32", or "live · 14:32" for a source with no delay to declare. */
function describe(quote: Quote): string {
  const at = new Date(quote.asOf).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
  return quote.delayMinutes > 0 ? `delayed ${quote.delayMinutes}m · ${at}` : `live · ${at}`
}
